/**
 * What the unified objective view shows about research, and which panel owns the live one. F-141.
 *
 * **Its own module with no DOM and no React in it, for `plan/step.ts`'s reason**, and with one
 * more reason of its own: `npm test` does not type-check `.tsx` at all — `tsconfig.test.json`
 * compiles a handful of React-free modules and nothing else — so a judgement written inline in
 * `Objectives.tsx` or `Research.tsx` is a judgement no test in this repository can see. The two
 * decisions below are exactly the kind that fail silently, which is this project's signature
 * failure, so they are here where a Node test can assert them.
 *
 * Leo, 2026-09-18, on the change this serves: *"We should not have two sections, research and
 * data and plan, rather have that unified in a single view that either provides access to plan
 * and research functionality, or shows dynamic data while loading, a better alternative than a
 * 15 minute loading spinner."*
 */

import type { PlanStep } from './step.js';

/**
 * Which face the research half of the view is wearing.
 *
 * It is one value rather than a set of booleans for `PlanStep`'s reason: the states are
 * exclusive, and two of them on screen at once is how a panel comes to say both "nothing has
 * been read" and "here is what was read".
 */
export type ResearchFace =
  /**
   * Neither request has answered yet.
   *
   * Distinct from `none`, and the distinction is the whole point of having it. "Nothing has been
   * read about this yet" is a claim, and making it in the half-second before the documents
   * request returns is a screen telling a learner their research is empty and then filling in —
   * the same flicker the curriculum's `undefined`-is-not-nought rule exists to prevent.
   */
  | 'unread'
  /** A run is going: the subjects, their states and what has landed so far. */
  | 'live'
  /** There is a run or a document to show, and nothing is arriving right now. */
  | 'found'
  /**
   * This plan is built from the learner's own pages, and nothing has been looked up.
   *
   * Only reachable with nothing to show, which is deliberate: `updatePlanSource` lets a learner
   * switch an objective to uploads *after* it has been researched (F-118), and that must not
   * take what was found off the screen. So the order below asks what exists before it asks what
   * the setting says.
   */
  | 'off'
  /** Research belongs to this objective and there is nothing from it yet. */
  | 'none';

/**
 * Which face to wear, given what the screen has actually read.
 *
 * `documents` is `undefined` until the request answers, which is not the same as nought — see
 * `unread` above.
 */
export function researchFace(input: {
  /** `planFrom === 'uploads'`: this objective never researches. `scoped` does, so it is not this. */
  uploadsOnly: boolean;
  /**
   * The learner has just pressed *start research* and no row records it yet — F-144's window.
   *
   * **First, and it is the finding a review caught.** `planStep` moves the step panel to
   * `researching` on this flag alone, so for up to one poll the screen said "we are reading up on
   * twelve subjects" at the top and "nothing has been read about this yet" a panel below it. Two
   * true-sounding sentences contradicting each other is worse than either, and the honest answer
   * is the one the flag already carries: something has started.
   */
  starting: boolean;
  /** Whether a research run row exists for this objective at all. */
  hasRun: boolean;
  /**
   * Whether the pipeline says material is still arriving — `stillComing`, the raw run state.
   *
   * **Deliberately not `researchArriving`**, which is the same question with an hour's staleness
   * window on it. That window exists to give a learner back a *button* a wedged run would
   * otherwise withhold for the session; nothing here withholds anything, and `live` and `found`
   * render the same panel. A face that flipped to `found` because a run had gone quiet would be
   * this module inventing a claim the pipeline has not made.
   */
  running: boolean;
  /**
   * How many research documents came back, or `undefined` before the request answers **or when
   * it failed**. A failed read is not an empty one: mapping it to nought is how a section says
   * "nothing has been read about this" over twelve researched subjects.
   */
  documents: number | undefined;
}): ResearchFace {
  if (input.starting || input.running) return 'live';
  if (input.hasRun || (input.documents ?? 0) > 0) return 'found';
  if (input.documents === undefined) return 'unread';
  if (input.uploadsOnly) return 'off';
  return 'none';
}

/**
 * Whether the research section draws the live panel, or the step panel above it already has.
 *
 * **Exactly one `ResearchPulse` may be on the screen, and before F-141 that was guaranteed by
 * the two panels being on two different routes.** Merging them removes that guarantee, and what
 * it would produce is precisely the failure F-129 was written to end: two lists of the same
 * twelve subjects, polled from one reading but rendered twice, where a learner counts subjects
 * in one and characters in the other and asks why they disagree.
 *
 * Keyed on the step rather than on the run state because the step is what decides whether the
 * panel above renders one: `researching` and `building` show it while the learner waits, and
 * `build` shows it under the offer to build a plan out of what was found (F-145). Every other
 * step renders no step panel at all, or one with no pulse in it, so the research section owns it.
 */
export function ownsPulse(step: PlanStep): boolean {
  return step !== 'researching' && step !== 'building' && step !== 'build';
}
